/**
 * PlaysVsBehaviorComparison
 *
 * Landing page marketing section — contrasts simple play-count tracking with
 * full listener-behavior analytics. Matches the Radio Solutions design system
 * (purple/orange palette, glass-morphism, lucide icons).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  XCircle,
  CheckCircle2,
  ListMusic,
  Activity,
  ArrowRight,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

// v2.9.203 — Comparison rows are keyed by their i18n labels so we can
// translate them in all 5 locales. The order is preserved.
const COMPARISON_KEYS = [
  { plays: 'pvbPlays1', behavior: 'pvbBehavior1' },
  { plays: 'pvbPlays2', behavior: 'pvbBehavior2' },
  { plays: 'pvbPlays3', behavior: 'pvbBehavior3' },
  { plays: 'pvbPlays4', behavior: 'pvbBehavior4' },
  { plays: 'pvbPlays5', behavior: 'pvbBehavior5' },
  { plays: 'pvbPlays6', behavior: 'pvbBehavior6' },
  { plays: 'pvbPlays7', behavior: 'pvbBehavior7' },
  { plays: 'pvbPlays8', behavior: 'pvbBehavior8' },
  { plays: 'pvbPlays9', behavior: 'pvbBehavior9' },
  { plays: 'pvbPlays10', behavior: 'pvbBehavior10' },
];

const COUNTER_DURATION = 2800; // ms — shared across animated stats

/** Animates a value 0 → target with easeOutCubic, triggered when ref enters viewport. */
const useAnimatedCounter = (target, duration = COUNTER_DURATION, threshold = 0.35) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || started) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStarted(true);
            observer.disconnect();
          }
        });
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [started, threshold]);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, target, duration]);

  return { ref, value, started };
};

const PlaysVsBehaviorComparison = () => {
  const { t } = useLanguage();
  const retention = useAnimatedCounter(30);

  return (
    <section
      className="relative py-16 lg:py-24 overflow-hidden"
      data-testid="plays-vs-behavior-section"
    >
      {/* Background accents — same palette as rest of landing */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/4 w-96 h-96 bg-gradient-to-br from-purple-500/15 to-pink-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 w-96 h-96 bg-gradient-to-br from-[#FF9F0A]/15 to-amber-500/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
            <Sparkles className="w-4 h-4 text-[#FF9F0A]" />
            <span className="text-sm font-medium text-white/90">
              {t('pvbWhyDifferent')}
            </span>
          </div>

          <h2
            className="text-2xl lg:text-3xl font-bold text-white mb-4"
            data-testid="plays-vs-behavior-title"
          >
            {t('pvbTitlePartA')}{' '}
            <span className="text-white/40 font-light italic">{t('pvbVs')}</span>{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9F0A] via-[#FFB84D] to-[#FF9F0A] whitespace-nowrap">
              {t('pvbTitlePartB')}
            </span>
          </h2>

          <p className="text-lg text-purple-200/90 leading-relaxed">
            {t('pvbDescription')}{' '}
            <span className="text-white font-semibold">
              {t('pvbWhatReallyWorks')}
            </span>
            {t('pvbDescriptionEnd')}
          </p>

          {/* Animated micro-stat */}
          <div
            ref={retention.ref}
            className={`mt-8 inline-flex items-center gap-3 pl-3 pr-5 py-2.5 rounded-full bg-gradient-to-r from-[#6B3FA0]/40 via-purple-800/30 to-[#FF9F0A]/20 border border-[#FF9F0A]/40 backdrop-blur-sm shadow-lg shadow-purple-900/30 transition-all duration-700 ${
              retention.started
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-3'
            }`}
            data-testid="retention-stat-pill"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF9F0A] to-[#FFB84D] flex items-center justify-center shrink-0 shadow-md shadow-orange-500/40">
              <TrendingUp className="w-4 h-4 text-[#2D0C4B]" strokeWidth={2.5} />
            </div>
            <div className="flex items-baseline gap-1.5 text-left">
              <span className="text-sm lg:text-base text-white/85 font-medium">
                {t('pvbRetentionStat')}
              </span>
              <span
                className="text-2xl lg:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D] tabular-nums"
                data-testid="retention-stat-value"
              >
                +{retention.value}%
              </span>
            </div>
          </div>
        </div>

        {/* Comparison grid */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* LEFT — Plays only */}
            <div
              className="relative rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm p-6 lg:p-8"
              data-testid="plays-column"
            >
              {/* Column header */}
              <div className="flex items-center gap-3 mb-6 pb-5 border-b border-white/10">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <ListMusic className="w-6 h-6 text-white/50" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/40 font-semibold mb-0.5">
                    {t('pvbTraditionalApproach')}
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white/80">
                    {t('pvbSimpleSongTrackingPlays')}
                  </h3>
                </div>
              </div>

              {/* Items */}
              <ul className="space-y-3.5" data-testid="plays-list">
                {COMPARISON_KEYS.map((row, idx) => (
                  <li
                    key={`plays-${idx}`}
                    className="flex items-start gap-3 text-white/65 leading-relaxed"
                  >
                    <XCircle className="w-5 h-5 text-rose-400/70 shrink-0 mt-0.5" />
                    <span className="text-sm lg:text-base">{t(row.plays)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* RIGHT — Listener Behavior (highlighted) */}
            <div
              className="relative rounded-2xl bg-gradient-to-br from-[#6B3FA0]/30 via-purple-900/20 to-[#FF9F0A]/10 border border-[#FF9F0A]/30 backdrop-blur-sm p-6 lg:p-8 shadow-2xl shadow-purple-900/20"
              data-testid="behavior-column"
            >
              {/* Glow accent */}
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#FF9F0A]/10 to-transparent pointer-events-none" />

              {/* Badge */}
              <div className="absolute -top-3 right-6 px-3 py-1 bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D] text-[#2D0C4B] text-xs font-bold rounded-full shadow-lg">
                RADIO SOLUTIONS
              </div>

              <div className="relative">
                {/* Column header */}
                <div className="flex items-center gap-3 mb-6 pb-5 border-b border-[#FF9F0A]/20">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF9F0A] to-[#FFB84D] flex items-center justify-center shrink-0 shadow-lg shadow-orange-500/30">
                    <Activity className="w-6 h-6 text-[#2D0C4B]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#FFB84D] font-semibold mb-0.5">
                      {t('pvbFullAnalysis')}
                    </div>
                    <h3 className="text-lg lg:text-xl font-bold text-white">
                      {t('pvbSongAndBehavior')}
                    </h3>
                  </div>
                </div>

                {/* Items */}
                <ul className="space-y-3.5" data-testid="behavior-list">
                  {COMPARISON_KEYS.map((row, idx) => (
                    <li
                      key={`beh-${idx}`}
                      className="flex items-start gap-3 text-white leading-relaxed"
                    >
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <span className="text-sm lg:text-base font-medium">
                        {t(row.behavior)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto mt-14 lg:mt-16 text-center">
          <h3
            className="text-xl lg:text-2xl font-bold text-white mb-3"
            data-testid="plays-vs-behavior-cta-title"
          >
            {t('pvbWantToKnowA')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D]">
              {t('pvbWhatReallyLiked')}
            </span>{' '}
            {t('pvbWantToKnowB')}
          </h3>
          <p className="text-base lg:text-lg text-purple-100/80 mb-7 leading-relaxed">
            {t('pvbContactPromo')}
          </p>

          <div className="flex justify-center">
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#FF9F0A] to-[#FFB84D] hover:from-[#FFB84D] hover:to-[#FF9F0A] text-[#2D0C4B] font-bold px-8 py-6 rounded-full shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-105 transition-all duration-300"
                data-testid="plays-vs-behavior-cta-button"
              >
                {t('pvbRequestDemo')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlaysVsBehaviorComparison;
